// BEGIN FILE: src/types/chartData.ts
// TypeScript interfaces for cached chart data (single source of truth)
// Stored in birth_profiles.chart_data JSONB column

import type { PlanetName } from '../utils/astroEngine';

export type VargaKey =
  | 'D1' | 'D2' | 'D3' | 'D4' | 'D7' | 'D9' | 'D10' | 'D12'
  | 'D16' | 'D20' | 'D24' | 'D27' | 'D30' | 'D40' | 'D45' | 'D60';

export type HouseNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface PlanetPosition {
  lon: number;                // Sidereal longitude (0-360°)
  speed: number;              // Deg/day
  retrograde: boolean;
  sign: number;               // 1-12
  signName: string;           // e.g., "Aries"
  nakshatra: string;          // e.g., "Ashwini"
  nakshatra_pada: number;     // 1-4
  house: number;              // 1-12
  offsetDeg: number;          // 0-30 (degrees within house)
}

export interface AscendantData {
  lon: number;                // Sidereal longitude
  sign: number;               // 1-12
  signName: string;
  nakshatra: string;
  nakshatra_pada: number;
}

export interface VargaData {
  ascSign: number;            // 1-12
  planetSigns: Record<PlanetName, number>;  // 1-12 per planet
}

export interface ShadbalaComponents {
  sthana: number;             // 0-100%
  dig: number;                // 0-100%
  kala: number;               // 0-100%
  cheshta: number;            // 0-100%
  naisargika: number;         // 0-100%
  drik: number;               // 0-100%
}

export interface ShadbalaData {
  components: ShadbalaComponents;
  total_pct: number;          // Total percentage
  virupa: number;             // Raw virupas
  ratio?: number;             // total/minimum (for 7 classical planets)
}

export interface IshtaKashtaData {
  uccha_bala: number;         // 0-60
  cheshta_bala: number;       // 0-60
  ishta: number;              // 0-60
  kashta: number;             // 0-60
  ishta_pct: number;          // 0-100%
  kashta_pct: number;         // 0-100%
}

export interface BhavaBalaData {
  lord_shadbala: number;
  occupant_strength: number;
  aspect_score: number;
  total: number;              // 0-100
}

export interface FiveFoldFriendship {
  from: PlanetName;
  to: PlanetName;
  score: number;              // -2 to +2
  classification: string;     // "Great Friend" | "Friend" | "Neutral" | "Enemy" | "Great Enemy"
}

export interface CombustionData {
  combust: boolean;
  separation_deg: number;
  threshold_deg: number;
}

export interface ExaltationData {
  exalted: boolean;
  debilitated: boolean;
  own_sign: boolean;
  moolatrikona: boolean;
  conjunct_exalt_lord: boolean;   // Enhanced dignity
  conjunct_debil_lord: boolean;
}

export interface MangalDoshaData {
  present: boolean;
  severity: 'high' | 'medium' | 'low' | null;
  houses: number[];           // Mars placement houses from Lagna/Moon/Venus
  cancellation_factors: string[];
}

export interface AspectData {
  from: PlanetName;
  from_house: number;         // Source house (1-12)
  to: number;                 // Target house (1-12)
  type: string;               // "7th aspect", "4th aspect", "5th aspect", etc.
  is_benefic: boolean;
}

export interface KaalSarpYogaData {
  present: boolean;
  type: 'full' | 'partial' | 'none';
  specific_type: string;      // e.g., "Anant Kaal Sarp"
  rahu_house: number;
  ketu_house: number;
  planets_between: PlanetName[];
  has_raj_yoga: boolean;
}

/**
 * Complete chart data structure saved to birth_profiles.chart_data
 * This is the single source of truth for all chart-related calculations
 */
export interface ChartData {
  // ===== METADATA =====
  version: string;              // Schema version (e.g., "1.0.0")
  calculated_at: string;        // ISO timestamp
  ayanamsa: {
    type: string;               // "lahiri" | "raman" | "kp" | "custom"
    value: number;              // Degrees at birth
  };
  
  // ===== BASIC ASTRONOMY =====
  utc_datetime: string;         // ISO UTC
  local_datetime: string;       // ISO in birth timezone
  julian_day?: number;          // Optional: JD for reference
  
  // ===== POSITIONS =====
  planets: Record<PlanetName, PlanetPosition>;
  
  placements?: Record<PlanetName, {
    house: number;
    sign: number;
    signDeg?: number;
    siderealLongitude?: number;
    retrograde?: boolean;
    nakshatra?: string;
  }>;  // Optional: detailed house placements for widget compatibility
  
  ascendantSign?: number;  // Optional: ascendant sign number (1-12) for widget compatibility
  
  ascendant: AscendantData;
  
  // ===== HOUSES =====
  house_system: 'whole-sign' | 'equal' | 'placidus';
  cusps: number[];              // 12 cusp degrees (0-360°)
  
  // ===== DIVISIONAL CHARTS =====
  vargas: Record<VargaKey, VargaData>;
  
  // ===== STRENGTH CALCULATIONS =====
  shadbala: Record<PlanetName, ShadbalaData>;
  
  ishta_kashta: Partial<Record<PlanetName, IshtaKashtaData>>;  // Excludes Rahu/Ketu
  
  bhava_bala?: Record<HouseNumber, BhavaBalaData>;  // Optional: may calculate on-demand
  
  five_fold_friendships: FiveFoldFriendship[];
  
  // ===== SPECIAL CONDITIONS =====
  combustion: Partial<Record<PlanetName, CombustionData>>;  // Excludes Sun
  
  exaltation: Record<PlanetName, ExaltationData>;
  
  mangal_dosha?: MangalDoshaData;  // Optional: marriage-specific
  
  // ===== ASPECTS =====
  aspects: AspectData[];
  
  // ===== HOUSE SCORES =====
  house_scores?: Record<HouseNumber, number>;  // Optional: 0-100 composite score
  
  // ===== FUNCTIONAL NATURE =====
  functional_nature: Record<PlanetName, 'benefic' | 'neutral' | 'malefic'>;
  
  // ===== KAAL SARP YOGA =====
  kaal_sarp_yoga?: KaalSarpYogaData;  // Optional: special yoga
  
  // ===== VIMSHOTTARI DASHA =====
  vimshottariTimeline?: Array<{
    level: 'MD' | 'AD' | 'PD' | 'SD';
    planet: PlanetName;
    house: number;
    rules: number[];
    aspects: number[];
    from: string;
    to: string;
  }>;  // Optional: current dasha snapshot (4 levels) for feeds generation

  // ===== BLENDED PLANET STRENGTH =====
  // Composite signed score per planet [-100..+100].
  // Formula (App.tsx useMemo): tanh(3 × sv01 × sb01 × dirEff) × 100
  //   where sv01 = shodashavarga calibrated pct / 100
  //         sb01 = shadbala total pct / 100
  //         dirEff = (ishta − kashta) / 60, clamped ±0.85
  // Includes dusthana, Viparita Raja Yoga, and natural malefic adjustments.
  // Used by push engine as continuous planet-strength multiplier.
  blended_planet_pct?: Partial<Record<PlanetName, number>>;
}

/**
 * Minimal validation for chart data version compatibility
 */
export const CURRENT_CHART_DATA_VERSION = '1.0.0';

export function isChartDataValid(data: any): data is ChartData {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.version === 'string' &&
    typeof data.calculated_at === 'string' &&
    data.planets &&
    data.ascendant &&
    data.cusps &&
    Array.isArray(data.cusps) &&
    data.cusps.length === 12
  );
}

export function isChartDataCurrent(data: ChartData): boolean {
  return data.version === CURRENT_CHART_DATA_VERSION;
}

/**
 * Check if cached data is still valid (less than 24 hours old for safety)
 * Natal charts are static, but we allow recalculation if schema changes
 */
export function isChartDataFresh(calculatedAt: string, maxAgeHours: number = 24 * 365): boolean {
  const cacheAge = Date.now() - new Date(calculatedAt).getTime();
  const maxAge = maxAgeHours * 60 * 60 * 1000;
  return cacheAge < maxAge;
}

// END FILE: src/types/chartData.ts
